// Firebase configuration and initialization
import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  onSnapshot,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Game collection reference
const GAMES_COLLECTION = 'billiards_games';

/**
 * Create a new game in Firestore
 */
export const createGame = async (gameId, hostPlayer, gameTypeId = 'billiards') => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  const gameData = {
    id: gameId,
    gameType: gameTypeId,
    status: 'waiting', // waiting, playing, finished
    hostId: hostPlayer.id,
    players: [hostPlayer],
    createdAt: Date.now(),
    winnerId: null,
    removedCards: [] // Track removed cards history
  };
  await setDoc(gameRef, gameData);
  return gameData;
};

/**
 * Join an existing game
 */
export const joinGame = async (gameId, player) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  const gameSnap = await getDoc(gameRef);
  
  if (!gameSnap.exists()) {
    throw new Error('Game not found');
  }
  
  const gameData = gameSnap.data();
  
  if (gameData.status !== 'waiting') {
    throw new Error('Game has already started');
  }
  
  if (gameData.players.length >= 8) {
    throw new Error('Game is full (max 8 players)');
  }
  
  await updateDoc(gameRef, {
    players: arrayUnion(player)
  });
  
  return gameData;
};

/**
 * Start the game and distribute cards
 */
export const startGame = async (gameId, players, distributedCards, gameTypeId = 'billiards', unoGameState = null, kittensGameState = null) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  // Update players with their cards
  const updatedPlayers = players.map((player, index) => ({
    ...player,
    cards: distributedCards[index],
    isWinner: false,
    hasCalledUno: false,
    isAlive: true // For exploding kittens
  }));
  
  const updateData = {
    status: 'playing',
    players: updatedPlayers
  };
  
  // Add UNO-specific game state
  if (gameTypeId === 'uno' && unoGameState) {
    updateData.discardPile = unoGameState.discardPile;
    updateData.drawPile = unoGameState.drawPile;
    updateData.currentPlayerIndex = 0;
    updateData.direction = 1; // 1 = clockwise, -1 = counter-clockwise
    updateData.pendingDrawCards = 0;
    updateData.pendingWildColor = null;
  }
  
  // Add Exploding Kittens-specific game state
  if (gameTypeId === 'explodingKittens' && kittensGameState) {
    updateData.drawPile = kittensGameState.drawPile;
    updateData.currentPlayerIndex = 0;
    updateData.skipDraw = false;
    updateData.attackActive = false;
    updateData.pendingDefuse = null;
    updateData.explodingKittensRemaining = kittensGameState.explodingKittensRemaining;
  }
  
  await updateDoc(gameRef, updateData);
};

/**
 * Remove a card from a player
 */
export const removeCard = async (gameId, playerId, updatedPlayers, winnerId = null, removedCardEntry = null) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  const updateData = {
    players: updatedPlayers
  };
  
  if (removedCardEntry) {
    updateData.removedCards = arrayUnion(removedCardEntry);
  }
  
  if (winnerId) {
    updateData.status = 'finished';
    updateData.winnerId = winnerId;
  }
  
  await updateDoc(gameRef, updateData);
};

/**
 * Remove last entry from removed cards (for undo)
 */
export const undoRemovedCard = async (gameId, updatedPlayers, updatedRemovedCards) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  await updateDoc(gameRef, {
    players: updatedPlayers,
    removedCards: updatedRemovedCards,
    status: 'playing',
    winnerId: null
  });
};

/**
 * Subscribe to game updates
 */
export const subscribeToGame = (gameId, callback) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  return onSnapshot(gameRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data());
    }
  });
};

/**
 * Reset game for a new round
 */
export const resetGame = async (gameId) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  const gameSnap = await getDoc(gameRef);
  
  if (!gameSnap.exists()) return;
  
  const gameData = gameSnap.data();
  const resetPlayers = gameData.players.map(p => ({
    ...p,
    cards: [],
    isWinner: false
  }));
  
  await updateDoc(gameRef, {
    status: 'waiting',
    players: resetPlayers,
    winnerId: null,
    removedCards: [] // Clear potted balls history when resetting game
  });
};

/**
 * Play a card in UNO game
 */
export const playUnoCard = async (
  gameId, 
  playerId, 
  cardIndex, 
  updatedPlayers, 
  discardPile, 
  drawPile,
  currentPlayerIndex,
  direction,
  pendingDrawCards,
  pendingWildColor,
  winnerId = null
) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  const updateData = {
    players: updatedPlayers,
    discardPile,
    drawPile,
    currentPlayerIndex,
    direction,
    pendingDrawCards: pendingDrawCards || 0,
    pendingWildColor: pendingWildColor || null
  };
  
  if (winnerId) {
    updateData.status = 'finished';
    updateData.winnerId = winnerId;
  }
  
  await updateDoc(gameRef, updateData);
};

/**
 * Draw card in UNO game
 */
export const drawUnoCard = async (
  gameId,
  updatedPlayers,
  drawPile,
  currentPlayerIndex
) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  await updateDoc(gameRef, {
    players: updatedPlayers,
    drawPile,
    currentPlayerIndex
  });
};

/**
 * Call UNO
 */
export const callUno = async (gameId, playerId, updatedPlayers) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  await updateDoc(gameRef, {
    players: updatedPlayers
  });
};

/**
 * Set wild card color
 */
export const setWildCardColor = async (gameId, color, currentPlayerIndex, direction) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  const gameSnap = await getDoc(gameRef);
  
  if (!gameSnap.exists()) return;
  
  const gameData = gameSnap.data();
  const discardPile = [...gameData.discardPile];
  
  // Update the top card's color
  if (discardPile.length > 0) {
    const UNO_COLORS = {
      red: '#DC2626',
      blue: '#2563EB',
      green: '#16A34A',
      yellow: '#FACC15',
    };
    
    discardPile[discardPile.length - 1] = {
      ...discardPile[discardPile.length - 1],
      color: UNO_COLORS[color],
      colorName: color
    };
  }
  
  await updateDoc(gameRef, {
    discardPile,
    pendingWildColor: null,
    currentPlayerIndex,
    direction
  });
};

/**
 * Play card(s) in Exploding Kittens game
 */
export const playKittensCard = async (
  gameId,
  playerId,
  cardIndices,
  updatedPlayers,
  drawPile,
  currentPlayerIndex,
  skipDraw,
  attackActive,
  winnerId = null
) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  const updateData = {
    players: updatedPlayers,
    drawPile,
    currentPlayerIndex,
    skipDraw: skipDraw || false,
    attackActive: attackActive || false
  };
  
  if (winnerId) {
    updateData.status = 'finished';
    updateData.winnerId = winnerId;
  }
  
  await updateDoc(gameRef, updateData);
};

/**
 * Draw card in Exploding Kittens game
 */
export const drawKittensCard = async (
  gameId,
  updatedPlayers,
  drawPile,
  currentPlayerIndex,
  pendingDefuse,
  winnerId = null,
  isAlive = true
) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  const updateData = {
    players: updatedPlayers,
    drawPile,
    currentPlayerIndex,
    skipDraw: false // Reset skip draw flag
  };
  
  if (pendingDefuse !== null && pendingDefuse !== undefined) {
    updateData.pendingDefuse = pendingDefuse;
  } else {
    updateData.pendingDefuse = null;
  }
  
  if (!isAlive) {
    updateData.status = 'playing'; // Player died, game continues
  }
  
  if (winnerId) {
    updateData.status = 'finished';
    updateData.winnerId = winnerId;
  }
  
  await updateDoc(gameRef, updateData);
};

/**
 * Place exploding kitten back in deck
 */
export const placeExplodingKitten = async (gameId, drawPile, position) => {
  const gameRef = doc(db, GAMES_COLLECTION, gameId);
  
  await updateDoc(gameRef, {
    drawPile,
    pendingDefuse: null,
    currentPlayerIndex: position // Use this field temporarily, will be updated
  });
};

export { db };


